import {
  useState,
  ReactNode,
  createContext,
  useMemo,
  useContext,
  useCallback,
  forwardRef,
  FC,
} from 'react'
import classNames from 'classnames'

import { Icon } from '../Icon'

import {
  AccordionContextState,
  AccordionProps,
  AccordionVariants,
  AccordionSize,
  IconPosition,
} from './types'

const defaultIconPositionMap: { [key in AccordionVariants]: IconPosition } = {
  basic: 'end',
  icon: 'start',
}

const defaultOpenIconMap: { [key in AccordionVariants]: ReactNode } = {
  basic: <Icon.ChevronUp color="#7D7D7D" className="rotate-0" />,
  icon: <Icon.Minus />,
}

const defaultClosedIconMap: { [key in AccordionVariants]: ReactNode } = {
  basic: <Icon.ChevronUp color="#7D7D7D" className="-rotate-180" />,
  icon: <Icon.Plus variant="light" />,
}

const variantSizes: { [key in AccordionSize]: string } = {
  sm: 'accordion--size-sm',
  lg: 'accordion--size-lg',
}

const AccordionContext = createContext<AccordionContextState>(
  {} as AccordionContextState,
)

export const Accordion = forwardRef<HTMLDivElement, AccordionProps>(
  (
    {
      children,
      exclusive = false,
      variant = 'basic',
      size = 'sm',
      iconPosition = defaultIconPositionMap[variant],
      openIcon = defaultOpenIconMap[variant],
      closedIcon = defaultClosedIconMap[variant],
      className,
      disableToggle = false,
      divider = true,
      id,
      keepMounted = false,
    }: AccordionProps,
    ref: React.Ref<HTMLDivElement>,
  ) => {
    const [openItems, setOpenItems] = useState<string[]>([])

    const handleClick = useCallback(
      (id: string, open: boolean) => {
        if (!disableToggle) {
          return exclusive
            ? setOpenItems(open ? [id] : [])
            : setOpenItems((prevItems) =>
                open
                  ? [...prevItems, id]
                  : prevItems.filter((item) => item !== id),
              )
        }
        setOpenItems([id])
      },
      [exclusive, disableToggle],
    )

    const context = useMemo<AccordionContextState>(
      () => ({
        openItems,
        handleClick,
        iconPosition,
        openIcon,
        closedIcon,
        size,
        disableToggle,
        divider,
        keepMounted,
      }),
      [
        openItems,
        handleClick,
        iconPosition,
        openIcon,
        closedIcon,
        size,
        disableToggle,
        divider,
        keepMounted,
      ],
    )

    return (
      <AccordionContext.Provider value={context}>
        <div
          id={id}
          ref={ref}
          className={classNames('accordion', variantSizes[size], className)}
        >
          {typeof children === 'function' ? children(context) : children}
        </div>
      </AccordionContext.Provider>
    )
  },
)

export const useAccordionContext = () => useContext(AccordionContext)
